import { isEqual } from 'lodash';

import { reportInteraction } from '@grafana/runtime';
import {
  NewSceneObjectAddedEvent,
  type SceneObject,
  SceneObjectBase,
  SceneObjectRemovedEvent,
  sceneGraph,
  StateCommittedEvent,
  type StateCommittedPayload,
} from '@grafana/scenes';
import { type ElementSelectionContextItem, type ElementSelectionOnSelectOptions } from '@grafana/ui';
import { getLayoutType } from 'app/features/dashboard/utils/tracking';

import { TabItem } from '../scene/layout-tabs/TabItem';
import { getRepeatCloneSourceKey } from '../utils/clone';
import { DashboardInteractions } from '../utils/interactions';
import { getDefaultVizPanel, getLayoutForObject, getDashboardSceneFor } from '../utils/utils';

import { ElementEditPane } from './ElementEditPane';
import {
  ConditionalRenderingChangedEvent,
  type DashboardBatchEditActionEventPayload,
  DashboardBatchEditActionEndEvent,
  DashboardBatchEditActionStartEvent,
  DashboardEditActionEvent,
  type DashboardEditActionEventPayload,
  DashboardStateChangedEvent,
  NewObjectAddedToCanvasEvent,
  ObjectRemovedFromCanvasEvent,
  ObjectsReorderedOnCanvasEvent,
  RepeatsUpdatedEvent,
} from './events';
import { DashboardOutline } from './outline/DashboardOutline';
import { type DashboardSidebarPane, type DashboardSidebarLike, type DashboardSidebarState } from './types';

export class DashboardSidebar extends SceneObjectBase<DashboardSidebarState> implements DashboardSidebarLike {
  public constructor(state?: Partial<DashboardSidebarState>) {
    super({
      selectionContext: {
        enabled: false,
        selected: [],
        onSelect: (item, options) => this.selectElement(item, options),
        onClear: () => this.clearSelection(),
      },
      isNewElement: false,
      undoStack: [],
      redoStack: [],
      outlinePane: state?.outlinePane ?? new DashboardOutline({}),
    });

    this.addActivationHandler(this.onActivate.bind(this));
  }

  private panelEditAction?: DashboardEditActionEvent;
  private _paneRequest?: AbortController;

  public beginPaneRequest(): AbortSignal {
    this.cancelPaneRequest();
    const controller = new AbortController();
    this._paneRequest = controller;
    if (!this.isActive) {
      this.cancelPaneRequest();
    }

    return controller.signal;
  }

  private cancelPaneRequest() {
    const request = this._paneRequest;
    this._paneRequest = undefined;
    request?.abort();
  }

  /** Set while a batch of edit actions is being collected, see startBatchAction/endBatchAction. */
  private _activeBatch?: {
    source: SceneObject;
    description?: string;
    actions: DashboardEditActionEventPayload[];
  };

  public setPanelEditAction(editAction: DashboardEditActionEvent) {
    this.panelEditAction = editAction;
  }

  public clone(withState: Partial<DashboardSidebarState>): this {
    // Clone without any undo/redo history
    return super.clone({ ...withState, redoStack: [], undoStack: [] });
  }

  private onActivate() {
    const dashboard = getDashboardSceneFor(this);

    this._subs.add(
      dashboard.subscribeToState((state, previous) => {
        if (
          state.isEditing !== previous.isEditing ||
          state.editview !== previous.editview ||
          state.editPanel !== previous.editPanel ||
          state.viewPanel !== previous.viewPanel
        ) {
          this.cancelPaneRequest();
        }
      })
    );

    if (dashboard.state.isEditing) {
      this.enableSelection();
    }

    this._subs.add(
      dashboard.subscribeToEvent(DashboardEditActionEvent, ({ payload }) => {
        this.handleEditAction(payload);
      })
    );

    this._subs.add(
      dashboard.subscribeToEvent(StateCommittedEvent, ({ payload }) => {
        this.handleStateCommitted(payload);
      })
    );

    this._subs.add(
      dashboard.subscribeToEvent(DashboardBatchEditActionStartEvent, ({ payload }) => {
        this.startBatchAction(payload);
      })
    );

    this._subs.add(
      dashboard.subscribeToEvent(DashboardBatchEditActionEndEvent, () => {
        this.endBatchAction();
      })
    );

    this._subs.add(
      dashboard.subscribeToEvent(NewObjectAddedToCanvasEvent, ({ payload }) => {
        this.newObjectAddedToCanvas(payload);
      })
    );

    this._subs.add(
      dashboard.subscribeToEvent(ObjectRemovedFromCanvasEvent, ({ payload }) => {
        this.clearSelection();
      })
    );

    this._subs.add(
      dashboard.subscribeToEvent(ObjectsReorderedOnCanvasEvent, ({ payload }) => {
        this.forceRender();
      })
    );

    this._subs.add(
      dashboard.subscribeToEvent(ConditionalRenderingChangedEvent, ({ payload }) => {
        this.forceRender();
      })
    );

    this._subs.add(
      dashboard.subscribeToEvent(RepeatsUpdatedEvent, () => {
        this.forceRender();
      })
    );

    if (this.panelEditAction) {
      this.performPanelEditAction(this.panelEditAction);
      this.panelEditAction = undefined;
    }

    return () => {
      if (this.state.selectionContext.selected.length) {
        this.clearSelection(true);
      }
      this.disableSelection();
    };
  }

  private performPanelEditAction(action: DashboardEditActionEvent) {
    // Some layout items are not yet active when leaving panel edit, let's wait for them to activate
    if (!action.payload.source.isActive) {
      trySwitchingToSourceTab(action.payload.source);
      setTimeout(() => this.performPanelEditAction(action));
      return;
    }

    action.payload.source.publishEvent(action, true);
  }

  private startBatchAction({ source, description }: DashboardBatchEditActionEventPayload) {
    if (this.state.redoStack.length > 0) {
      this.setState({ redoStack: [] });
    }

    this._activeBatch = { source, description, actions: [] };
  }

  private endBatchAction() {
    const batch = this._activeBatch;
    this._activeBatch = undefined;

    if (!batch || batch.actions.length === 0) {
      return;
    }

    const action: DashboardEditActionEventPayload = {
      source: batch.source,
      description: batch.description,
      perform: () => {
        batch.actions.forEach((childAction) => this.performAction(childAction));
      },
      undo: () => {
        [...batch.actions].reverse().forEach((childAction) => this.undoSingleAction(childAction));
      },
    };

    this.setState({ undoStack: [...this.state.undoStack, action] });
  }

  /**
   * Handles all edit actions
   * Adds to undo history and selects new object
   */
  private handleEditAction(action: DashboardEditActionEventPayload, skipPerform = false) {
    if (this._activeBatch) {
      this._activeBatch.actions.push(action);
      if (!skipPerform) {
        this.performAction(action);
      }
      return;
    }
    // Clear redo stack when user performs a new action
    // Otherwise things can get into very broken states
    if (this.state.redoStack.length > 0) {
      this.setState({ redoStack: [] });
    }

    if (!skipPerform) {
      this.performAction(action);
    }

    this.setState({ undoStack: [...this.state.undoStack, action] });
  }

  /**
   * Any SceneObject can perform state changes inside the object (e.g., drag and drop or resize).
   * To make such changes undoable SceneObject can provide a closure to revert and replay
   * the change. Since the change already happens inside SceneObject we skip perform and just add
   * the action to the stack.
   * @private
   */
  private handleStateCommitted(payload: StateCommittedPayload) {
    this.handleEditAction(
      {
        source: payload.source,
        description: payload.description,
        perform: payload.replay,
        undo: payload.revert,
      },
      true
    );
    payload.source.publishEvent(new DashboardStateChangedEvent({ source: payload.source }), true);
  }

  /**
   * Removes last action from undo stack and adds it to redo stack.
   */
  public undoAction() {
    const undoStack = this.state.undoStack.slice();
    const action = undoStack.pop();
    if (!action) {
      return;
    }

    this.undoSingleAction(action);

    this.setState({ undoStack, redoStack: [...this.state.redoStack, action] });
    reportInteraction('grafana_dashboard_undo');
  }

  private undoSingleAction(action: DashboardEditActionEventPayload) {
    action.undo();
    action.source.publishEvent(new DashboardStateChangedEvent({ source: action.source }), true);

    if (action.addedObject) {
      this.clearSelection();
    }

    if (action.movedObject && action.selectOnMove !== false) {
      this.selectObject(action.movedObject, { force: true });
    }

    if (action.removedObject) {
      this.newObjectAddedToCanvas(action.removedObject);
    }
  }

  /**
   * Some edit actions also require clearing selection or selecting new objects
   */
  private performAction(action: DashboardEditActionEventPayload) {
    action.perform();
    action.source.publishEvent(new DashboardStateChangedEvent({ source: action.source }), true);

    if (action.addedObject) {
      this.newObjectAddedToCanvas(action.addedObject);
    }

    if (action.movedObject && action.selectOnMove !== false) {
      this.selectObject(action.movedObject, { force: true });
    }

    // If action removed an object and not added a new one we need to update selection
    if (action.removedObject && !action.addedObject) {
      // But only if removed object is currently selected
      if (action.removedObject === this.getSelectedObject()) {
        this.fixSelectionOfRemovedObject();
      }
    }
  }

  /**
   * Removes last action from redo stack and adds it to undo stack.
   */
  public redoAction() {
    const redoStack = this.state.redoStack.slice();
    const action = redoStack.pop();
    if (!action) {
      return;
    }

    this.performAction(action);

    this.setState({ redoStack, undoStack: [...this.state.undoStack, action] });
    reportInteraction('grafana_dashboard_redo');
  }

  public enableSelection() {
    if (this.state.selectionContext.enabled) {
      return;
    }

    this.setState({ selectionContext: { ...this.state.selectionContext, enabled: true } });
  }

  public disableSelection() {
    this.cancelPaneRequest();
    if (!this.state.selectionContext.enabled) {
      return;
    }

    this.setState({
      selectionContext: { ...this.state.selectionContext, selected: [], enabled: false },
      openPane: undefined,
    });
  }

  private selectElement(element: ElementSelectionContextItem, options: ElementSelectionOnSelectOptions) {
    let obj = sceneGraph.findByKey(this, element.id);
    if (!obj) {
      console.warn('Cannot find element by key="%s"!', element.id);
      return;
    }

    const sourceKey = getRepeatCloneSourceKey(obj);
    if (sourceKey) {
      obj = sceneGraph.findByKey(this, sourceKey);
      if (!obj) {
        console.warn('Cannot find element by source key="%s"!', sourceKey);
        return;
      }
    }

    this.selectObject(obj, options);
  }

  public selectObject(obj: SceneObject, { multi, force }: ElementSelectionOnSelectOptions = {}) {
    this.cancelPaneRequest();
    const id = obj.state.key!;
    const hasItem = this.state.selectionContext.selected.find((i) => i.id === id);

    // Special logic for tabs only select tab of open pane is not already open or tab is already active
    if (!force && !this.state.openPane && obj instanceof TabItem && !obj.isCurrentTab()) {
      return;
    }

    let selectedDisconnectedObject: SceneObject | undefined;
    if (obj.getRoot() !== this.getRoot() || obj.parent === this) {
      selectedDisconnectedObject = obj;
    }

    // If current open pane is not showing selected element, then we should maintain selection (force = true) which disables selection toggling
    if (this.state.openPane?.getId() !== 'element') {
      force = true;
    }

    if (multi) {
      if (hasItem) {
        // Remove item unless force is true
        if (!force) {
          this.updateSelection(
            this.state.selectionContext.selected.filter((i) => i.id !== id),
            selectedDisconnectedObject
          );
        }
      } else {
        this.updateSelection([...this.state.selectionContext.selected, { id }], selectedDisconnectedObject);
      }
    } else {
      if (hasItem && !force) {
        this.updateSelection([], selectedDisconnectedObject);
      } else {
        this.updateSelection([{ id }], selectedDisconnectedObject);
      }
    }
  }

  public fixSelectionOfRemovedObject() {
    if (this.state.previousState) {
      this.goBackToPrevious();
    } else {
      this.clearSelection(true);
    }
  }

  public goBackToPrevious() {
    this.cancelPaneRequest();
    if (!this.state.previousState) {
      return;
    }

    this.setState({
      selectionContext: this.state.previousState.selectionContext,
      openPane: this.state.previousState.openPane,
      selectedDisconnectedObject: this.state.previousState.selectedDisconnectedObject,
      previousState: this.state.previousState.previousState,
    });

    if (this.state.openPane?.getId() === 'element' && this.state.selectionContext.selected.length === 1) {
      const selectedObj = this.getSelectedObject();
      if (selectedObj) {
        void import(/* webpackChunkName: "dashboard-edit-actions" */ '../actions/utils/getEditableElementFor').then(
          ({ getEditableElementFor }) => {
            const element = getEditableElementFor(selectedObj);
            element?.scrollIntoView?.();
          }
        );
      }
    }
  }

  private updateSelection(selected: ElementSelectionContextItem[], selectedDisconnectedObject?: SceneObject) {
    // onBlur events are not fired on unmount and some sidebar inputs have important onBlur events
    // This make sure they fire before unmounting
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    const newState: DashboardSidebarState = {
      ...this.state,
      selectionContext: { ...this.state.selectionContext, selected },
      openPane: selected.length ? new ElementEditPane({}) : undefined,
      isNewElement: false,
      selectedDisconnectedObject,
    };

    this.setState({
      ...newState,
      previousState: selected.length ? getStateForPaneHistory(this.state, newState) : undefined,
    });
  }

  /**
   * Look-up selected object by key. If key is not provided, will return object based on current selection.
   * @param key of the object
   * @returns
   */
  public getSelectedObject(key?: string): SceneObject | undefined {
    if (key) {
      // Not using findByKey here as it requires try catch in case object is not found
      return sceneGraph.findObject(this, (obj) => obj.state.key === key) ?? undefined;
    }

    if (this.state.selectedDisconnectedObject) {
      return this.state.selectedDisconnectedObject;
    }

    if (this.state.selectionContext.selected.length === 0) {
      return undefined;
    }

    // Not using findByKey here as it requires try catch in case object is not found
    return (
      sceneGraph.findObject(this, (obj) => obj.state.key === this.state.selectionContext.selected[0].id) ?? undefined
    );
  }

  /**
   * @param force If force = true it will clear selection even when docked
   * @returns
   */
  public clearSelection(force = false) {
    this.cancelPaneRequest();
    if (!this.state.selectionContext.selected.length) {
      return;
    }

    // If we are docked then clearing selection should select dashboard itself
    // Unless the user explicitly closes pane
    if (this.state.isDocked && !force) {
      const dashboard = getDashboardSceneFor(this);
      if (this.getSelectedObject() !== dashboard) {
        this.selectObject(dashboard);
      }
      return;
    }

    this.updateSelection([]);
  }

  public openPane(openPane: DashboardSidebarPane) {
    this.cancelPaneRequest();
    if (this.state.openPane?.getId() === openPane.getId()) {
      this.setState({ openPane: undefined });
      return;
    }

    this.setState({ openPane, previousState: getStateForPaneHistory(this.state) });

    // UrlSyncManager subscribes to this and syncs url state with pane state
    this.publishEvent(new NewSceneObjectAddedEvent(openPane), true);
  }

  public closePane() {
    this.cancelPaneRequest();
    if (this.state.selectionContext.selected.length) {
      this.clearSelection(true);
    }

    if (this.state.openPane) {
      const openPane = this.state.openPane;
      this.setState({ openPane: undefined });

      // UrlSyncManager subscribes to this and removes the pane url state from url
      this.publishEvent(new SceneObjectRemovedEvent(openPane), true);
    }
  }

  private newObjectAddedToCanvas(obj: SceneObject) {
    this.selectObject(obj, { force: true });
    this.setState({ isNewElement: true });
  }

  public async addNewPanel(target: SceneObject | undefined) {
    const panel = await getDefaultVizPanel();
    const dashboard = getDashboardSceneFor(this);

    if (target) {
      const layout = getLayoutForObject(target) ?? dashboard;
      layout.addPanel(panel);
    } else {
      dashboard.addPanel(panel);
    }

    DashboardInteractions.trackAddPanelClick('sidebar', getLayoutType(target));
  }

  public pastePanel(target: SceneObject | undefined) {
    const dashboard = getDashboardSceneFor(this);

    if (target) {
      const layout = getLayoutForObject(target) ?? dashboard;
      layout.pastePanel();
    } else {
      dashboard.pastePanel();
    }
  }
}

function trySwitchingToSourceTab(source: SceneObject) {
  if (source.parent === undefined) {
    return;
  }

  if (source.parent instanceof TabItem) {
    const tab = source.parent;
    const tabsLayout = source.parent.getParentLayout();
    if (tabsLayout.state.currentTabSlug !== tab.getSlug()) {
      tabsLayout.switchToTab(tab);
    }
  } else {
    trySwitchingToSourceTab(source.parent);
  }
}

function getStateForPaneHistory(
  currentState: DashboardSidebarState | undefined,
  newState?: DashboardSidebarState
): DashboardSidebarState | undefined {
  if (!currentState || !currentState.openPane) {
    return undefined;
  }

  if (currentState.openPane?.excludeFromHistory) {
    return getStateForPaneHistory(currentState.previousState!, newState);
  }

  // If newState is same dont create an duplcate history entry
  if (
    newState &&
    newState.openPane?.getId() === currentState.openPane?.getId() &&
    isEqual(newState.selectionContext.selected, currentState.selectionContext.selected)
  ) {
    return getStateForPaneHistory(currentState.previousState, newState);
  }

  return currentState;
}
